// Reading one row of the LCV participant spreadsheet.
//
// The browser parses the file, so this is the whole understanding of the sheet: which header means
// which field, what makes a row unusable, and which rows are deliberately left alone.
//
// Two rules shape everything here:
//
//  - Only a row whose status is Existing Pro+ is imported. Everything else is ignored outright and
//    never validated, because none of its other columns are read. It is not an error and must
//    never be shown as one, or an administrator goes looking for a fault that is not there.
//  - Nothing here decides a domain question. Whether the person already exists, whether the dealer
//    is known, and every upsert rule belong to the backend. This only asks whether the row is
//    filled in well enough to send.

// The supplied file carries trailing spaces on most headers, so a header is matched on its start
// rather than compared whole.
const COLUMN_ALIASES = {
     dealerCode: ["dlr code", "dealer code"],
     area: ["area"],
     areaManager: ["lcv area manager", "area manager"],
     areaManagerEmail: ["area manager email"],
     dealerName: ["dlr name", "dealer name"],
     status: ["status"],
     contact: ["contact"],
     firstName: ["contact first name"],
     surname: ["contact surname"],
     email: ["email"],
     role: ["role"],
     accredited: ["accredited"],
     userId: ["user id"],
};

// Read and thrown away on purpose. Dashboard access is driven by job role, so this column must not
// reach the backend.
const IGNORED_COLUMNS = ["managers dashboard"];

export const LCV_ROLE_NAMES = [
     "Pro+ Sales Specialist",
     "Pro+ Manager",
     "Fleet Manager",
     "General Manager",
];

export const LCV_STATUS_EXISTING = "Existing Pro+";
export const LCV_STATUS_PROSPECT = "Pro+ Prospect";

export const ROW_VALID = "valid";
export const ROW_INVALID = "invalid";
export const ROW_SKIPPED = "skipped";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const flatten = (value) =>
     String(value === null || value === undefined ? "" : value)
          .replace(/\s+/g, " ")
          .trim()
          .toLowerCase();

// "Area " matches "area", and "Contact first name " must win over "Contact " for the same sheet, so
// the longest alias is tried first.
const headerMatches = (header, aliases) => {
     const flat = flatten(header);
     return aliases.some((alias) => flat.startsWith(alias));
};

const fieldForHeader = (header) => {
     if (IGNORED_COLUMNS.some((ignored) => flatten(header).startsWith(ignored))) {
          return "";
     }

     const candidates = Object.keys(COLUMN_ALIASES)
          .filter((field) => headerMatches(header, COLUMN_ALIASES[field]))
          .sort((a, b) => longestAlias(b) - longestAlias(a));

     return candidates.length ? candidates[0] : "";
};

const longestAlias = (field) =>
     COLUMN_ALIASES[field].reduce((longest, alias) => Math.max(longest, alias.length), 0);

const readCell = (value) =>
     String(value === null || value === undefined ? "" : value).trim();

// The role is typed by hand, so "Pro+ sales specialist " and "PRO+ SALES SPECIALIST" both have to
// resolve. Returns the canonical name, or an empty string when it is not an LCV role.
export const normaliseRole = (value) => {
     const flat = flatten(value);
     const match = LCV_ROLE_NAMES.find((name) => flatten(name) === flat);
     return match || "";
};

// Returns the canonical status, or an empty string when the value is neither of the two.
export const normaliseStatus = (value) => {
     const flat = flatten(value);

     if (flat === flatten(LCV_STATUS_EXISTING)) {
          return LCV_STATUS_EXISTING;
     }

     if (flat === flatten(LCV_STATUS_PROSPECT)) {
          return LCV_STATUS_PROSPECT;
     }

     return "";
};

// Y or N, and a blank cell means N. Blank is safe to read as N because accreditation is one way: a
// later file can only turn it on, never off.
// Returns "Y", "N", or an empty string when the cell holds something that is neither.
export const normaliseAccredited = (value) => {
     const flat = flatten(value);

     if (flat === "") {
          return "N";
     }

     if (flat === "y" || flat === "yes") {
          return "Y";
     }

     if (flat === "n" || flat === "no") {
          return "N";
     }

     return "";
};

const emptyFields = () => ({
     dealerCode: "",
     area: "",
     areaManager: "",
     areaManagerEmail: "",
     dealerName: "",
     status: "",
     contact: "",
     firstName: "",
     surname: "",
     email: "",
     role: "",
     accredited: "",
     userId: "",
});

// A row typed straight into the grid rather than read from a sheet. Every column starts empty so
// nothing is inherited from the row above and mistaken for something the file said. The status is
// the one exception: it is born Existing Pro+, because a row with any other status is skipped and
// would never be sent.
export const blankRow = (sheetRowNumber) => ({
     ...emptyFields(),
     sheetRowNumber,
     status: LCV_STATUS_EXISTING,
});

// One sheet row, as SheetJS hands it over, becomes one editable grid row. The sheet row number is
// captured here and carried, so removing a row on screen never shifts what the others report.
export const buildRow = (sheetRow, sheetRowNumber) => {
     const row = emptyFields();

     Object.keys(sheetRow).forEach((header) => {
          const field = fieldForHeader(header);

          if (field) {
               row[field] = readCell(sheetRow[header]);
          }
     });

     return {
          ...row,
          sheetRowNumber,
          status: normaliseStatus(row.status) || row.status,
          // Some rows carry only the combined "Contact" name and leave the split columns empty, so
          // it stands in for the first name rather than leaving the row looking nameless.
          firstName: row.firstName || row.contact,
          role: normaliseRole(row.role) || row.role,
          accredited: normaliseAccredited(row.accredited) || row.accredited,
     };
};

// Only a row at an Existing Pro+ dealer is imported. Anything else in the status column, Prospect
// or blank or unrecognised, is ignored outright: no user, no participant, no invitation, and no
// validation either, since nothing on such a row is ever read.
export const isSkippedRow = (row) => normaliseStatus(row.status) !== LCV_STATUS_EXISTING;

// The problems a row can have before it is worth sending, keyed by the field that carries each one
// so the grid can colour the offending cell rather than the whole row. Domain verdicts are not here.
export const rowFieldProblems = (row) => {
     const problems = {};

     if (!row.dealerCode) {
          problems.dealerCode = "Dealer code missing";
     }

     if (!row.area) {
          problems.area = "Area missing";
     } else if (!/^\d+$/.test(row.area)) {
          problems.area = "Area must be a number";
     }

     if (!row.firstName) {
          problems.firstName = "First name missing";
     }

     if (!row.email) {
          problems.email = "Email missing";
     } else if (!EMAIL_PATTERN.test(row.email)) {
          problems.email = "Email is not a valid address";
     }

     if (!normaliseRole(row.role)) {
          problems.role = "Pick one of the four LCV roles";
     }

     if (!normaliseAccredited(row.accredited)) {
          problems.accredited = "Accredited must be Y or N";
     }

     return problems;
};

export const rowProblem = (row) => Object.values(rowFieldProblems(row)).join("; ");

export const rowState = (row) => {
     if (isSkippedRow(row)) {
          return ROW_SKIPPED;
     }

     return rowProblem(row) ? ROW_INVALID : ROW_VALID;
};

// The three numbers shown above the grid. Skipped rows are their own count and are deliberately
// kept out of the invalid one.
export const countRowStates = (rows) =>
     rows.reduce(
          (counts, row) => {
               const state = rowState(row);
               return { ...counts, [state]: counts[state] + 1 };
          },
          { [ROW_VALID]: 0, [ROW_INVALID]: 0, [ROW_SKIPPED]: 0 },
     );

// What actually gets posted. The sheet's own dealer code, area and role name travel as text,
// because a dealership may not exist yet and resolving them here would reject rows the backend
// would accept. The row number counts the header, so it matches what the sheet shows.
export const toCommitRows = (rows) =>
     rows.filter((row) => rowState(row) === ROW_VALID).map((row) => ({
          rowNumber: row.sheetRowNumber,
          dealerCode: row.dealerCode,
          area: row.area,
          dealerName: row.dealerName,
          status: normaliseStatus(row.status),
          firstName: row.firstName,
          lastName: row.surname,
          email: row.email,
          role: normaliseRole(row.role),
          accredited: normaliseAccredited(row.accredited),
          areaManagerName: row.areaManager,
          areaManagerEmail: row.areaManagerEmail,
     }));
